import styled from "styled-components";
import { AlertTriangle, Check, ClipboardCheck, X } from "lucide-react";
import React from "react";

import { theme } from "./theme";
import { useT } from "./i18n";

/* Filing a housekeeping checkout report from the front desk.
 *
 * The fields come from the server as they are right now and their ids are posted back
 * verbatim: the server's "a not-secured room needs a note" gate reads the form's live
 * fields, so a cached list here would let the two disagree about the same report.
 */
const Backdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1600;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  max-width: 520px;
  width: 100%;
`;

const Head = styled.div`
  align-items: center;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.45rem;
  padding: 0.85rem 1.1rem;
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  flex: 1 1 auto;
  font-size: 1rem;
  font-weight: 800;
  gap: 0.4rem;
  margin: 0;
`;

const Close = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.ink700};
  cursor: pointer;
  padding: 0.1rem;
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

const Lead = styled.p`
  color: ${theme.ink700};
  font-size: 0.84rem;
  line-height: 1.5;
  margin: 0 0 0.75rem;
`;

const Field = styled.div`
  border-top: 1px solid ${theme.border};
  padding: 0.6rem 0;

  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }
`;

const Label = styled.label`
  align-items: center;
  color: ${theme.ink};
  cursor: pointer;
  display: flex;
  font-size: 0.86rem;
  font-weight: 700;
  gap: 0.45rem;
`;

const Category = styled.div`
  color: #5e6278;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  margin-bottom: 0.15rem;
  text-transform: uppercase;
`;

const Input = styled.input`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.86rem;
  margin-top: 0.3rem;
  padding: 0.4rem 0.55rem;
  width: 100%;
`;

const TextArea = styled.textarea`
  border: 1px solid
    ${(props) => (props.$required ? theme.danger : theme.border)};
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.86rem;
  margin-top: 0.3rem;
  min-height: 70px;
  padding: 0.4rem 0.55rem;
  resize: vertical;
  width: 100%;
`;

const Hint = styled.div`
  color: ${(props) => (props.$alert ? theme.danger : "#5e6278")};
  font-size: 0.76rem;
  font-weight: ${(props) => (props.$alert ? 700 : 600)};
  margin-top: 0.25rem;
`;

const Warn = styled.div`
  align-items: flex-start;
  background: #fdf3e3;
  border-radius: 9px;
  color: #8a5a00;
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 0.35rem;
  line-height: 1.45;
  margin-top: 0.7rem;
  padding: 0.5rem 0.65rem;
`;

const Foot = styled.div`
  border-top: 1px solid ${theme.border};
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.75rem 1.1rem;
`;

const Btn = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 0.3rem;
  padding: 0.5rem 0.95rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Ghost = styled(Btn)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Ok = styled(Btn)`
  background: ${theme.gold};
  border: 0;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: ${theme.goldDark};
  }
`;

// props: { roomCode, fields, busy, claimDevice, onSubmit, onCancel }
const HkCheckoutDialog = (props) => {
  const { t } = useT();
  const fields = props.fields || [];
  const [values, setValues] = React.useState({});

  const valueFor = (field) => values[field.id];
  const setValue = (field, value) =>
    setValues((previous) => ({ ...previous, [field.id]: value }));

  const checkbox = fields.find((field) => field.field_type === "checkbox");
  const textarea = fields.find((field) => field.field_type === "textarea");
  const secured = checkbox ? valueFor(checkbox) === true : true;
  const noteRequired = Boolean(checkbox && textarea && !secured);
  const noteMissing = noteRequired && !String(valueFor(textarea) || "").trim();

  const responses = fields.map((field) => {
    const value = valueFor(field);
    if (field.field_type === "checkbox") {
      return { field_id: field.id, secured: value === true };
    }
    if (field.field_type === "textarea") {
      return { field_id: field.id, text: String(value || "") };
    }
    if (field.field_type === "quantity") {
      return { field_id: field.id, quantity: String(value ?? "") };
    }
    return { field_id: field.id, status: String(value || "") };
  });

  const untouched = fields.some(
    (field) =>
      field.field_type !== "textarea" && typeof valueFor(field) === "undefined",
  );

  return (
    <Backdrop onClick={props.onCancel}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            <ClipboardCheck size={16} />
            {t("hk_file_title").replace("%{room}", props.roomCode || "—")}
          </Title>
          <Close type="button" aria-label={t("close")} onClick={props.onCancel}>
            <X size={18} />
          </Close>
        </Head>

        <Body>
          <Lead>{t("hk_file_lead")}</Lead>

          {fields.map((field) => (
            <Field key={field.id}>
              {field.category ? <Category>{field.category}</Category> : null}

              {field.field_type === "checkbox" ? (
                <>
                  <Label>
                    <input
                      type="checkbox"
                      checked={valueFor(field) === true}
                      onChange={(event) =>
                        setValue(field, event.target.checked)
                      }
                    />
                    {field.item_name}
                  </Label>
                  <Hint $alert={valueFor(field) === false}>
                    {valueFor(field) === false
                      ? t("hk_file_not_secured_hint")
                      : t("hk_file_secured_hint")}
                  </Hint>
                </>
              ) : null}

              {field.field_type === "textarea" ? (
                <>
                  <Label htmlFor={`hk-${field.id}`}>{field.item_name}</Label>
                  <TextArea
                    id={`hk-${field.id}`}
                    $required={noteMissing}
                    value={String(valueFor(field) || "")}
                    placeholder={t("hk_file_note_placeholder")}
                    onChange={(event) => setValue(field, event.target.value)}
                  />
                  {noteMissing ? (
                    <Hint $alert>{t("hk_file_note_required")}</Hint>
                  ) : null}
                </>
              ) : null}

              {field.field_type === "quantity" ? (
                <>
                  <Label htmlFor={`hk-${field.id}`}>{field.item_name}</Label>
                  <Input
                    id={`hk-${field.id}`}
                    type="number"
                    min="0"
                    value={String(valueFor(field) ?? "")}
                    placeholder={String(field.default_quantity ?? "")}
                    onChange={(event) => setValue(field, event.target.value)}
                  />
                  <Hint>
                    {t("hk_file_expected").replace(
                      "%{count}",
                      String(field.default_quantity ?? "—"),
                    )}
                  </Hint>
                </>
              ) : null}

              {field.field_type === "condition" ? (
                <>
                  <Label htmlFor={`hk-${field.id}`}>{field.item_name}</Label>
                  <Input
                    as="select"
                    id={`hk-${field.id}`}
                    value={String(valueFor(field) || "")}
                    onChange={(event) => setValue(field, event.target.value)}
                  >
                    <option value="">—</option>
                    <option value="baik">baik</option>
                    <option value="rusak">rusak</option>
                    <option value="hilang">hilang</option>
                  </Input>
                </>
              ) : null}
            </Field>
          ))}

          {props.claimDevice ? (
            <Warn>
              <AlertTriangle size={15} style={{ flex: "0 0 auto" }} />
              <span>
                {t("hk_file_claim_warning").replace(
                  "%{device}",
                  props.claimDevice,
                )}
              </span>
            </Warn>
          ) : null}
        </Body>

        <Foot>
          <Ghost type="button" onClick={props.onCancel}>
            <X size={15} /> {t("confirm_cancel")}
          </Ghost>
          <Ok
            type="button"
            disabled={props.busy || noteMissing || untouched}
            onClick={() =>
              props.onSubmit({
                responses,
                confirm_takeover: Boolean(props.claimDevice),
              })
            }
          >
            <Check size={15} />{" "}
            {props.claimDevice ? t("hk_file_takeover_ok") : t("hk_file_ok")}
          </Ok>
        </Foot>
      </Card>
    </Backdrop>
  );
};

export default HkCheckoutDialog;
